<template>
    <!-- V2 ONBOARDING LAYOUT (EXPERIMENT) -->
    <onboarding-v2-layout
        v-if="isInOnboardingV2Experiment"
        :error-text="errorText"
        :has-back="false"
        :has-header="false"
        :show-progress-bar="false"
    >
        <div class="v2-title-container">
            <h5 class="v2-title-bold">
                Welcome!
            </h5>
            <p class="v2-title-regular">
                Enter your mobile number
            </p>
        </div>
        <form-container
            id="SignInForm"
            ref="signInFormRef"
            class="v2-form-container"
        >
            <form-field-phone
                id="phoneNumber"
                label="Mobile Number"
                ref="phoneField"
                placeholder="Mobile Number"
                name="phoneNumber"
                input-type="number"
                v-model="phoneNumber"
                data-testid="phone-number"
                autocomplete="off"
                focus-event="event_aven_advisor_phone_number_field_focus"
                :dark-mode="true"
                @input="onPhoneNumberInput"
            />
            <form-field
                id="inviteCode"
                class="mt-2"
                v-model="inviteCode"
                v-if="shouldShowPaidReferralsOption"
                name="inviteCode"
                placeholder="Invite Code (Optional)"
                label="Invite Code (Optional)"
                data-testid="invite-code"
                :dark-mode="true"
            />
            <ack-terms-button
                button-classes="mt-2 btn tw-w-full"
                :disabled="phoneNumberCannotBeSubmitted"
                :submitting="submitting"
                :dark-mode="true"
                @click="handleOnSubmit"
                @error="handleAckTermsError"
                :docs="[
                    {
                        docType: DocumentType.termsOfService,
                        signature: phoneNumber,
                        signatureType: DocumentSignatureType.text,
                    },
                ]"
                data-testid="continue-button"
            >
                <template #cta>
                    Continue
                </template>

                <template #additionalAcknowledgements>
                    <onboarding-v2-footnote class="mb-1">
                        By providing your number and clicking "<ack-terms-cta />", you consent to automated or manual marketing messages & to the
                        <document-render-link
                            :doc-type="DocumentType.termsOfService"
                            title="Terms of Service"
                        >
                            Terms of Service
                        </document-render-link>. Consent to marketing messages is not required
                        for any purchase. Text 'stop' to (415) 610-8313 to opt out.
                    </onboarding-v2-footnote>
                </template>
            </ack-terms-button>
        </form-container>
        <div class="v2-footer-link-container">
            Phone Number Changed?
            <a
                class="v2-footer-link small fw-normal"
                data-testid="login-with-email-button"
                event-name="click_button_choose_login_with_email"
                @click="handleLoginWithEmailClick"
                @keydown="handleLoginWithEmailClick"
            >
                Login with email
            </a>
        </div>
        <modal
            v-if="!!visibleDisclosure"
            :title="visibleDisclosure.title"
            :show="!!visibleDisclosure"
            @close="toggleVisibleDisclosure"
        >
            <div v-html="visibleDisclosure.body" />
        </modal>
    </onboarding-v2-layout>

    <!-- ORIGINAL ONBOARDING LAYOUT (NON-EXPERIMENT) -->
    <onboarding-layout
        v-else
        :error-text="errorText"
        :has-back="false"
    >
        <div class="d-flex flex-column align-items-center">
            <img
                src="@/assets/images/advisor-logo.svg"
                alt="Aven Logo"
                class="w-50"
            >
            <img
                src="@/assets/images/landing-promo-image.png"
                alt="Aven promo image"
                class="w-100"
            >
            <h5 class="fw-light text-center">
                Enter your mobile number to get your financial profile
            </h5>
        </div>
        <form-container
            id="SignInForm"
            ref="signInFormRef"
        >
            <form-field-phone
                id="phoneNumber"
                class="mt-2"
                label="Mobile Number"
                ref="phoneField"
                placeholder="Mobile Number"
                name="phoneNumber"
                input-type="number"
                v-model="phoneNumber"
                data-testid="phone-number"
                autocomplete="off"
                focus-event="event_aven_advisor_phone_number_field_focus"
                @input="onPhoneNumberInput"
            />
            <form-field
                id="inviteCode"
                class="mt-2"
                v-model="inviteCode"
                v-if="shouldShowPaidReferralsOption"
                name="inviteCode"
                placeholder="Invite Code (Optional)"
                label="Invite Code (Optional)"
                data-testid="invite-code"
            />
            <ack-terms-button
                button-classes="mt-2 btn btn-primary tw-w-full"
                :disabled="phoneNumberCannotBeSubmitted"
                :submitting="submitting"
                @click="handleOnSubmit"
                @error="handleAckTermsError"
                :docs="[
                    {
                        docType: DocumentType.termsOfService,
                        signature: phoneNumber,
                        signatureType: DocumentSignatureType.text,
                    },
                ]"
                data-testid="continue-button"
            >
                <template #cta>
                    Continue
                </template>

                <template #additionalAcknowledgements>
                    <div class="small text-muted mt-2 mb-1">
                        By providing your number and clicking "<ack-terms-cta />", you consent to automated or manual marketing messages & to the
                        <document-render-link
                            :doc-type="DocumentType.termsOfService"
                            title="Terms of Service"
                        >
                            Terms of Service
                        </document-render-link>. Consent to marketing messages is not required
                        for any purchase. Text 'stop' to (415) 610-8313 to opt out.
                    </div>
                </template>
            </ack-terms-button>
        </form-container>
        <a
            class="btn btn-link gray-link small fw-normal p-2 w-100"
            data-testid="login-with-email-button"
            event-name="click_button_choose_login_with_email"
            @click="handleLoginWithEmailClick"
            @keydown="handleLoginWithEmailClick"
        >
            Phone number changed? Login with email
        </a>
        <modal
            v-if="!!visibleDisclosure"
            :title="visibleDisclosure.title"
            :show="!!visibleDisclosure"
            @close="toggleVisibleDisclosure"
        >
            <div v-html="visibleDisclosure.body" />
        </modal>
    </onboarding-layout>
</template>

<script>
    import OnboardingLayout from '@/layouts/Onboarding'
    import OnboardingV2Layout from '@/layouts/OnboardingV2'
    import FormContainer from '@/components/base/FormContainer'
    import {
        requestTwoFactor,
        RequestTwoFaErrors,
        routeToAuthFlow,
        AuthFlow,
        getSessionInfo,
        validateInviteCode,
        checkHasGottenRefereeBonus,
        postGoogleOfflineConversion,
        postFacebookOfflineConversion,
        copyReferrerPromos,
        copyReferrerPolicies,
        assignPromosFromDMInviteCode,
    } from '@/services/api'
    import { inspect, log } from '@/utils/logger'
    import FormFieldPhone from '@/components/base/FormFieldPhone'
    import { mapFields } from 'vuex-map-fields'
    import Modal from '@/components/Modal'
    import { onboardingRoutePaths } from '@/routes/onboardingRoutes'
    import { phoneNumberDisclosure } from 'aven_shared/src/legal/evenFinancialDisclosures'
    import { getOnboardingRoutePath, routeToNotificationIfPermsNotGranted } from '@/utils/navigationUtil'
    import { endTimeToInteractiveTimer } from '@/utils/timeToInteractive'
    import { DocumentType, trySendAppsFlyerHomeownerPhoneNumberSubmitSuccessEvent, trySendAppsFlyerPhoneNumberSubmitSuccessEvent } from '@/nativeWebInteraction/nativeWebInteraction'
    import { sharedRoutePaths } from '@/routes/sharedRoutes'
    import FormField from 'aven_shared/src/components/FormField'
    import { signInRoutePaths } from '@/routes/signInRoutes'
    import _ from 'lodash'
    import { logEvent } from '@/utils/http-client'
    import { FacebookPixelStep, GoogleConversionName, HomeOwnershipStatus, DocumentSignatureType } from 'aven_types'
    import { canTriggerAppsFlyerEventForHomeownerPhoneNumberSubmitSuccess, canTriggerAppsFlyerEventForPhoneNumberSubmitSuccess } from '@/nativeWebInteraction/nativeFeatureAvailability'
    import AckTermsButton from '../components/AckTermsButton.vue'
    import DocumentRenderLink from '@/components/DocumentRenderLink.vue'
    import AckTermsCta from '@/components/AckTermsCta.vue'
    import { appLocalStorage, localStorageKey } from '@/utils/storage'
    import { useOnboardingV2Store } from '@/pinia/onboardingV2/useOnboardingV2Store'
    import OnboardingV2Footnote from '@/components/onboardingV2/OnboardingV2Footnote.vue'

    const GENERIC_ERROR = 'Something went wrong. Please make sure your phone number is correct and try again!'
    const ACCOUNT_BLOCKED_ERROR = 'Your account is blocked'

    export default {
        name: 'Landing',
        components: { FormField, FormContainer, AckTermsButton, FormFieldPhone, OnboardingLayout, OnboardingV2Layout, Modal, DocumentRenderLink, AckTermsCta, OnboardingV2Footnote },
        props: {
            isWebOG: {
                type: Boolean,
                default: false,
            },
        },
        data: function () {
            return {
                DocumentType,
                DocumentSignatureType,
                submitting: false,
                errorText: '',
                lastSubmittedPhoneNumber: null,
                visibleDisclosure: null,
                phoneNumberDisclosure,
                isDeveloperModeTurnedOn: false,
            }
        },
        computed: {
            ...mapFields(['phoneNumber', 'inviteCode', 'signUpGoal', 'homeOwnershipStatus']),
            onboardingV2Store: function () {
                return useOnboardingV2Store()
            },
            isInOnboardingV2Experiment: function () {
                return this.onboardingV2Store.isAdvisorLeadInOnboardingV2Experiment
            },
            phoneNumberCannotBeSubmitted: function () {
                // We obviously can't submit when the form is submitting, but we also don't
                // want to resubmit a phone number that our server rejected for some reason.
                return this.submitting || this.phoneNumber === this.lastSubmittedPhoneNumber || !this.phoneNumber
            },
            shouldShowPaidReferralsOption: function () {
                return this.$featureFlagConfig.paidReferrals
            },
        },
        mounted: async function () {
            const experimentsOverrides = this.$store.getters.experimentsOverrides
            log.info('Experiments overrides: %{experimentsOverrides}', { experimentsOverrides: JSON.stringify(experimentsOverrides) })
            const isAccountBlocked = this.$route.query.isAccountBlocked
            this.$logEvent('view_advisor_landing_page', { isAccountBlocked, isInOnboardingV2Experiment: this.isInOnboardingV2Experiment })
            if (this.isWebOG) {
                this.$logEvent('view_advisor_landing_page_web_og', { isAccountBlocked, isInOnboardingV2Experiment: this.isInOnboardingV2Experiment })
            }
            if (isAccountBlocked) {
                log.info('Showing Aven Advisor account block error on Landing.vue')
                this.errorText = ACCOUNT_BLOCKED_ERROR
            }
            await endTimeToInteractiveTimer(this.$route)

            // get isWebOG from props
            this.$store.commit('setIsWebOG', { isWebOG: this.isWebOG })
        },
        methods: {
            handleLoginWithEmailClick: async function () {
                this.$logEvent('click_button_choose_login_with_email')
                if (this.isWebOG) {
                    this.$logEvent('click_button_choose_login_with_email_web_og')
                }
                this.phoneNumber = ''
                await this.$router.push({ path: signInRoutePaths.SIGN_IN_EMAIL })
            },
            handleAckTermsError: function (error) {
                this.errorText = error
                this.$logEvent('event_aven_advisor_tos_ack_terms_failure', { error })
            },
            handleOnSubmit: async function () {
                try {
                    const phoneNumber = this.phoneNumber
                    log.info('Aven Advisor user entered phoneNumber %{phoneNumber} on landing page. Requesting 2FA code.', { phoneNumber })

                    const isValid = await this.$refs.signInFormRef.$refs.observer.validate()
                    this.$logEvent('click_button_submit_phone_number', { isValid })
                    if (this.isWebOG) {
                        this.$logEvent('click_button_submit_phone_number_web_og', { isValid })
                    }
                    if (!isValid) {
                        this.$logEvent('event_aven_advisor_submit_phone_failure')
                        if (this.isWebOG) {
                            this.$logEvent('event_aven_advisor_submit_phone_failure_web_og')
                        }
                        log.info('Aven Advisor user did not enter phone number on landing page')
                        return
                    }

                    this.errorText = ''
                    this.submitting = true

                    if (this.inviteCode) {
                        let { shouldContinue } = await this.validateInviteCode(this.inviteCode)
                        const hasGottenRefereeBonus = await this.checkHasGottenRefereeBonus()
                        shouldContinue = shouldContinue && !hasGottenRefereeBonus
                        if (!shouldContinue) {
                            return
                        }
                        await this.assignPromos(this.inviteCode, phoneNumber)
                    }

                    // Keep track of the last submitted phone number so we can stop the user from submitting the
                    // same phone number over and over when the server tells us there's something wrong with it.
                    this.lastSubmittedPhoneNumber = phoneNumber
                    const response = await requestTwoFactor({
                        phoneNumber,
                    })

                    if (!response.data.success) {
                        this.$logEvent('event_aven_advisor_submit_phone_failure')
                        if (this.isWebOG) {
                            this.$logEvent('event_aven_advisor_submit_phone_failure_web_og')
                        }
                        if (response.data.error === RequestTwoFaErrors.TOO_MANY_2FA_REQUESTS) {
                            this.errorText = 'Too many attempts, please try again in 10 minutes'
                        } else {
                            this.errorText = GENERIC_ERROR
                        }
                        log.info('Failed to request a 2FA code for phone %{phoneNumber}. Error %{error}', { phoneNumber, error: response.data.error })
                        return
                    }

                    // Get phone number based experiments by updating session info with phone number
                    window.onboardingPhoneNumber = phoneNumber
                    appLocalStorage.setItem(localStorageKey.phoneNumber, phoneNumber)
                    await getSessionInfo()

                    log.info('Received Aven Advisor 2FA code for phone %{phoneNumber}', { phoneNumber })
                    this.$store.commit('updateOtpSid', {
                        otpSid: response.data.payload.sid,
                    })
                    log.info('Updating esign consent git hash and method fi tos git hash to %{releaseHash}', { releaseHash: process.env.VUE_APP_RELEASE_HASH })
                    this.$store.commit('updateEsignConsentGitHash', { hash: process.env.VUE_APP_RELEASE_HASH })
                    this.$store.commit('updateConsentToMethodFiTosGitHash', { hash: process.env.VUE_APP_RELEASE_HASH })

                    this.$logEvent('click_button_submit_aven_advisor_app_phone_number')
                    if (this.isWebOG) {
                        this.$logEvent('click_button_submit_aven_advisor_app_phone_number_web_og')
                    }
                    if (this.homeOwnershipStatus === HomeOwnershipStatus.OWN) {
                        await postGoogleOfflineConversion(phoneNumber, GoogleConversionName.advisorHomeownerLead)
                    }

                    const routeResponse = await routeToAuthFlow(phoneNumber)
                    if (!routeResponse.data.success) {
                        // Currently this should never happen. If the API call succeeds, routeResponse.data.success will
                        // be true. However, to account for the future possibility that isn't the case, we'll include
                        // basic error handling.
                        this.errorText = GENERIC_ERROR
                        log.error('Failed to route to auth on Aven Advisor. Error %{error}', { error: routeResponse.data.error })
                        this.$logEvent('event_aven_advisor_submit_phone_failure', { error: routeResponse.data.error })
                        return
                    }
                    this.$logEvent('event_aven_advisor_submit_phone_success')
                    if (this.isWebOG) {
                        this.$logEvent('event_aven_advisor_submit_phone_success_web_og')
                    }
                    // By default, we route to the auth flow that the server tells us to.
                    this.$store.commit('setAuthFlow', { authFlow: routeResponse.data.payload.authFlow })
                    if (routeResponse.data.payload.authFlow === AuthFlow.ONBOARDING) {
                        await postFacebookOfflineConversion(phoneNumber, FacebookPixelStep.PhoneNumberSubmit)
                        if (canTriggerAppsFlyerEventForPhoneNumberSubmitSuccess()) {
                            trySendAppsFlyerPhoneNumberSubmitSuccessEvent()
                        }

                        if (this.homeOwnershipStatus === HomeOwnershipStatus.OWN && canTriggerAppsFlyerEventForHomeownerPhoneNumberSubmitSuccess()) {
                            trySendAppsFlyerHomeownerPhoneNumberSubmitSuccessEvent()
                        }
                    }
                    await this.routeToAuthFlow(routeResponse.data.payload.authFlow)
                } catch (e) {
                    // Assume the server never received the number, so we'll allow resubmission. In practice this might
                    // not be exactly correct (e.g. an invalid number could cause the server to return a 500), but this
                    // helps us in the case of a network error, for example, because it lets the user resubmit.
                    this.lastSubmittedPhoneNumber = null
                    this.errorText = GENERIC_ERROR
                    log.fatal('Error submitting phone number on Aven Advisor landing page', { error: e })
                } finally {
                    this.submitting = false
                }
            },
            togglePhoneNumberDisclosure: function () {
                this.toggleVisibleDisclosure(phoneNumberDisclosure)
            },
            toggleVisibleDisclosure: function (disclosure) {
                this.visibleDisclosure = this.visibleDisclosure ? null : disclosure
                if (this.visibleDisclosure) {
                    this.$logEvent('click_button_advisor_disclosure', {
                        type: disclosure.type,
                        text: disclosure.body,
                    })
                }
            },
            routeToAuthFlow: async function (authFlow) {
                // When the account exists w/ a password, the flow is phone -> Push perms -> OTP -> password, so we'll go to OTP next
                if (authFlow === AuthFlow.SIGN_IN) {
                    log.info('Aven Advisor user has an account, so moving through sign in flow')
                    await routeToNotificationIfPermsNotGranted(this.$router, sharedRoutePaths.ENABLE_NOTIFICATION_SIGN_IN, sharedRoutePaths.OTP_VERIFICATION)
                }
                // When the account exists w/ out a password, and indicating from OG reject, the flow is phone -> Push perms -> OTP -> ssn/dob -> new password, so we'll go to OTP next
                // This flow is currently ONLY for people who are denied in origination and automatically made an Advisor account
                else if (authFlow === AuthFlow.OG_REJECT_ONBOARDING) {
                    log.info('Aven Advisor user has an account, but no password, and has sourceApplicantId, so moving through OG reject onboarding flow')
                    await routeToNotificationIfPermsNotGranted(this.$router, sharedRoutePaths.ENABLE_NOTIFICATION_PASSWORD_RESET, sharedRoutePaths.OTP_VERIFICATION)
                }
                // When the account exists w/ out a password, and not from OG reject, then the user finished most of the onboarding -
                // but hasn't set the password. The flow is phone -> Push perms -> OTP -> ssn/dob -> new password, so we'll go to OTP next
                else if (authFlow === AuthFlow.CONTINUE_ONBOARDING) {
                    log.info('Aven Advisor user has an account, but no password, and has no sourceApplicantId, so moving through continue onboarding flow')
                    await routeToNotificationIfPermsNotGranted(this.$router, sharedRoutePaths.ENABLE_NOTIFICATION_PASSWORD_RESET, sharedRoutePaths.OTP_VERIFICATION)
                } else if (authFlow === AuthFlow.WEB_ORIGINATION_TO_NATIVE) {
                    log.info('Aven Advisor user has an account, but no password, and is coming from web origination, so moving through web origination to native flow')
                    await routeToNotificationIfPermsNotGranted(this.$router, sharedRoutePaths.ENABLE_NOTIFICATION_SIGN_IN, sharedRoutePaths.OTP_VERIFICATION)
                }
                // For backward compat
                else if (authFlow === AuthFlow.PASSWORD_RESET) {
                    log.info('Aven Advisor user has an account, but no password, so moving through password reset flow')
                    await routeToNotificationIfPermsNotGranted(this.$router, sharedRoutePaths.ENABLE_NOTIFICATION_PASSWORD_RESET, sharedRoutePaths.OTP_VERIFICATION)
                }
                // When the account doesn't exist, the flow is phone -> multiple data collection pages -> OTP -> password,
                // so we'll start the data collection with the name page.
                else if (authFlow === AuthFlow.ONBOARDING) {
                    log.info('Aven Advisor user does not have an account, so moving through onboarding flow')

                    const potentialPath = await getOnboardingRoutePath(sharedRoutePaths.ROOT)
                    if (potentialPath) {
                        return await this.$router.push({ path: potentialPath })
                    }

                    await this.$router.push({ path: onboardingRoutePaths.FULL_NAME })
                } else {
                    log.fatal('Unexpected Aven Advisor auth flow: %{authFlow}', { authFlow })
                }
            },
            validateInviteCode: async function () {
                log.info('Validating invite code %{inviteCode}', { inviteCode: this.inviteCode })
                try {
                    const validateInviteCodeResponse = await validateInviteCode(this.inviteCode)
                    this.$logEvent('event_advisor_lead_entered_invite_code', { inviteCode: this.inviteCode })

                    if (!validateInviteCodeResponse.data.payload.isValid) {
                        log.info('Invite code %{inviteCode} is not valid', { inviteCode: this.inviteCode })
                        this.errorText = validateInviteCodeResponse.data.payload.message ?? `Invite code ${this.inviteCode} is not valid. Please enter a different invite code.`
                        this.$logEvent('event_advisor_lead_entered_invite_code_failure', { inviteCode: this.inviteCode })
                        return { shouldContinue: false }
                    }
                    log.info('Invite code %{inviteCode} is valid.', { inviteCode: this.inviteCode })
                    this.$logEvent('event_advisor_lead_entered_invite_code_success', { inviteCode: this.inviteCode })
                    return { shouldContinue: true }
                } catch (error) {
                    log.fatal('Error validating invite code', { error })
                    this.$logEvent('event_advisor_lead_entered_invite_code_failure', { inviteCode: this.inviteCode, error: JSON.stringify(error) })

                    // Allow the user to continue forward, even if we failed to validate the invite code to minimize disruption to onboarding
                    return { shouldContinue: true }
                }
            },
            checkHasGottenRefereeBonus: async function () {
                try {
                    const response = await checkHasGottenRefereeBonus(this.phoneNumber)
                    if (response.data.payload.hasGottenRefereeBonus) {
                        log.info('User has already gotten referee bonus')
                        this.errorText = 'The phone number you entered has already received a referral bonus. Please enter a different phone number.'
                        this.$logEvent('event_advisor_lead_entered_invite_code_failure', { inviteCode: this.inviteCode })
                    }
                    return response.data.payload.hasGottenRefereeBonus
                } catch (e) {
                    log.fatal('Error checking if user has gotten referee bonus', { error: e })
                    return false
                }
            },
            onPhoneNumberInput: (() => {
                let hasStartedTyping = false
                const debouncedFinishTyping = _.debounce((phoneNumber) => {
                    if (phoneNumber.length === 10) {
                        logEvent('event_aven_advisor_finished_typing_phone')
                    }
                }, 500)

                return (phoneNumber) => {
                    if (!hasStartedTyping && phoneNumber.length >= 1) {
                        hasStartedTyping = true
                        logEvent('event_aven_advisor_started_typing_phone')
                    }

                    debouncedFinishTyping(phoneNumber)
                }
            })(),
            assignPromos: async function (inviteCode, refereePhoneNumber) {
                log.info('assignPromos => Assigning promos for invite code %{inviteCode} and referee phone number %{refereePhoneNumber}', { inviteCode, refereePhoneNumber })
                await Promise.all([
                    this.copyReferrerPolicies(inviteCode, refereePhoneNumber),
                    this.copyReferrerPromos(inviteCode, refereePhoneNumber) /* This function is for backwards compatibility - we no longer assign promos as of May 2025*/,
                    this.assignCampaignPromos(inviteCode, refereePhoneNumber) /* This function is for backwards compatibility - we no longer assign promos as of May 2025*/,
                ])
            },
            copyReferrerPolicies: async function (inviteCode, refereePhoneNumber) {
                try {
                    const response = await copyReferrerPolicies(inviteCode, refereePhoneNumber)
                    if (response?.data?.success) {
                        log.info('Successfully copied %{copiedPolicies} policies given inviteCode %{inviteCode}', { copiedPolicies: inspect(response.data.payload.copiedPolicies), inviteCode })
                        this.$logEvent('event_advisor_transfer_referrer_free_stuff_policies_success', { inviteCode: this.inviteCode })
                    } else {
                        throw new Error(response.data.error)
                    }
                } catch (error) {
                    log.info('Error copying referrer policies given inviteCode %{inviteCode} with error message: %{errorMessage}', { inviteCode, errorMessage: error.message })
                    this.$logEvent('event_advisor_transfer_referrer_free_stuff_policies_failure', { inviteCode: this.inviteCode, error: JSON.stringify(error) })
                }
            },
            copyReferrerPromos: async function (inviteCode, refereePhoneNumber) {
                try {
                    const response = await copyReferrerPromos(inviteCode, refereePhoneNumber)
                    if (response?.data?.success) {
                        log.info('Successfully copied %{copiedPromosCount} referrer promos given inviteCode %{inviteCode}', { copiedPromosCount: response.data.payload.copiedPromosCount, inviteCode })
                        this.$logEvent('event_advisor_transfer_referrer_promos_success', { inviteCode: this.inviteCode })
                    } else {
                        log.info('Error copied referrer promos given inviteCode %{inviteCode} with response message: %{error}', { inviteCode, error: response.data.error })
                        this.$logEvent('event_advisor_transfer_referrer_promos_failure', { inviteCode: this.inviteCode })
                    }
                } catch (error) {
                    log.info('Error copied referrer promos given inviteCode %{inviteCode} with error message: %{errorMessage}', { inviteCode, errorMessage: error.message })
                    this.$logEvent('event_advisor_transfer_referrer_promos_failure', { inviteCode: this.inviteCode, error: JSON.stringify(error) })
                }
            },
            assignCampaignPromos: async function (inviteCode, refereePhoneNumber) {
                try {
                    const response = await assignPromosFromDMInviteCode(inviteCode, refereePhoneNumber)
                    if (response?.data?.success) {
                        log.info('Successfully transferred %{assignedPromosCount} campaign linked promo(s) given inviteCode %{inviteCode}', {
                            assignedPromosCount: response.data.payload.assignedPromosCount,
                            inviteCode,
                        })
                        this.$logEvent('event_advisor_assign_campaign_promos_success', { inviteCode: this.inviteCode })
                    } else {
                        log.info('Error transferring referrer promos given inviteCode %{inviteCode} with response message: %{error}', { inviteCode, error: response.data.error })
                        this.$logEvent('event_advisor_assign_campaign_promos_failure', { inviteCode: this.inviteCode })
                    }
                } catch (error) {
                    log.info('Error transferring referrer promos given inviteCode %{inviteCode} with error message: %{errorMessage}', { inviteCode, errorMessage: error.message })
                    this.$logEvent('event_advisor_assign_campaign_promos_failure', { inviteCode: this.inviteCode, error: JSON.stringify(error) })
                }
            },
        },
    }
</script>

<style lang="scss" scoped>
    @import '@/styles/onboardingV2.scss';

    a {
        text-decoration: underline !important;
    }
    .blue-link {
        color: #0054d5;
    }
    .gray-link {
        color: $gray-400;
    }
    .white-link {
        color: white;
    }
</style>
